import React, { useCallback, useEffect, useState } from 'react';
import {
  ReloadOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { servicesService } from '../../services';
import { BackupRecoveryPoint } from '../../services/services.service';
import { useNotificationContext } from '../../contexts/NotificationContext';

/**
 * Tab de respaldos (AWS Backup) para servicios EC2 (VMs y app-deploys).
 * Fase 1: visibilidad (lista puntos de recuperación) + respaldo on-demand.
 * Solo operable si el servicio tiene el addon de respaldos (backupEnabled).
 * La restauración se construye en una fase posterior.
 */

interface BackupsTabProps {
  serviceId: string;
}

const fmtBytes = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || '').toUpperCase();
  const map: Record<string, { cls: string; icon: React.ReactNode }> = {
    COMPLETED: { cls: 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30', icon: <CheckCircleOutlined /> },
    RUNNING: { cls: 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/30', icon: <LoadingOutlined /> },
    PARTIAL: { cls: 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/30', icon: <ClockCircleOutlined /> },
  };
  const cfg = map[s] || { cls: 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700', icon: <ClockCircleOutlined /> };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.icon} {s || 'UNKNOWN'}
    </span>
  );
};

const BackupsTab: React.FC<BackupsTabProps> = ({ serviceId }) => {
  const { success, error: showError } = useNotificationContext();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [points, setPoints] = useState<BackupRecoveryPoint[]>([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await servicesService.getBackups(serviceId);
      setEnabled(resp.backupEnabled);
      setPoints(resp.recoveryPoints);
    } catch (e) {
      console.error('Error cargando respaldos:', e);
      showError('No se pudieron cargar los respaldos');
    } finally {
      setLoading(false);
    }
  }, [serviceId, showError]);

  useEffect(() => { load(); }, [load]);

  const handleBackupNow = async () => {
    setCreating(true);
    try {
      await servicesService.createBackup(serviceId);
      success('Respaldo iniciado. Aparecerá en la lista en unos minutos.');
    } catch (e: any) {
      console.error('Error al respaldar:', e);
      showError(e?.message || 'No se pudo iniciar el respaldo');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-8 justify-center">
        <LoadingOutlined /> Cargando respaldos…
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-200 mb-1">Respaldos no activos</p>
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Este servicio no tiene el addon de <strong>Respaldos diarios</strong>. Con él, la
              instancia se respalda automáticamente cada día (retención de 7 días) y puedes lanzar
              respaldos manuales. Actívalo contratando el addon en la configuración del servicio.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <DatabaseOutlined /> Puntos de recuperación
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Respaldo diario automático · retención 7 días
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <ReloadOutlined /> Refrescar
          </button>
          <button
            onClick={handleBackupNow}
            disabled={creating}
            className="px-3 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          >
            <CloudUploadOutlined /> {creating ? 'Iniciando…' : 'Respaldar ahora'}
          </button>
        </div>
      </div>

      {points.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
          Aún no hay puntos de recuperación. El primer respaldo diario corre a las 06:00 UTC, o puedes
          lanzar uno ahora con «Respaldar ahora».
        </div>
      ) : (
        <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left font-medium px-4 py-2">Fecha</th>
                <th className="text-left font-medium px-4 py-2">Estado</th>
                <th className="text-right font-medium px-4 py-2">Tamaño</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {points.map((p) => (
                <tr key={p.recoveryPointArn} className="bg-white dark:bg-gray-900">
                  <td className="px-4 py-2 text-gray-900 dark:text-white">{fmtDate(p.createdAt)}</td>
                  <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{fmtBytes(p.sizeBytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        La restauración a partir de un punto de recuperación estará disponible próximamente.
      </p>
    </div>
  );
};

export default BackupsTab;
